import {
  baseContour,
  type WSIPassportType,
  type AIHandlerResultType,
  type MeasurementsType,
} from '@tig-dpqa-cloud/contract-backend-dataplane';
import type {
  GeoJSONContourType,
  xyCoordinates,
  SlideScanGeneratedMetadataType,
} from '@tig-dpqa-cloud/contract-backend-frontend';
import { match, P } from 'ts-pattern';
import z from 'zod';

const measurementContours = z.array(baseContour).optional().describe('List of contours measured by the AI handler');
type MeasurementContoursType = z.infer<typeof measurementContours>;

type XYContoursGroupType = z.infer<z.ZodArray<typeof xyCoordinates>>;
type XYCoordinatesType = z.infer<typeof xyCoordinates>;

// Maps a point computed on the pre-rotation portrait macro onto the landscape macro actually displayed.
// Rotation of 90° CCW: [x, y] → [y, portraitWidth - x]
// Rotation of 270° CCW: [x, y] → [portraitHeight - y, x] (pivots on the portrait height, not width)
export const rotateMacroContourPoint =
  ([x, y]: XYCoordinatesType, portraitWidth: number, portraitHeight: number, rotation: number): XYCoordinatesType => {
    if (rotation === 270) {
      return [portraitHeight - y, x];
    }
    // Default to 90° CCW mapping
    return [y, portraitWidth - x];
  };

const generateGeoJsonFromContours = (
  contours: MeasurementContoursType,
  offset: { x: number; y: number } | undefined = undefined,
  portraitWidth: number | undefined = undefined,
  portraitHeight: number | undefined = undefined,
  rotation: number | undefined = undefined,
): GeoJSONContourType[] | undefined => {
  if (!contours) {
    return undefined;
  }
  return Object.values(
    contours.reduce(
      (acc, curr, index) => {
        const idx = curr.hierarchy[3] === -1 ? index : curr.hierarchy[3];
        if (!(idx in acc)) {
          acc[idx] = [];
        }
        // Rotate and offset each contour point as needed
        acc[idx].push(
          curr.contour
            // Rotate each contour point if a portrait width/height is provided
            .map((c) => (
              portraitWidth !== undefined && portraitHeight !== undefined && rotation !== undefined
                ? rotateMacroContourPoint(c, portraitWidth, portraitHeight, rotation)
                : c
            ))
            // Apply offset after rotation
            .map((c) => (offset ? [Math.max(0, c[0] + offset.x), Math.max(0, c[1] + offset.y)] : c)),
        );
        return acc;
      },
      {} as { [parentIdx: string]: XYContoursGroupType[] },
    ),
  ).map((c) => ({
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: c,
    },
  }));
};

const determineMacroShift = (metadata: WSIPassportType): { x: number; y: number } | undefined => {
  return match(metadata)
    .with(
      {
        macro: {
          croppedArea: P.select('cropped', P.not(P.nullish)),
          originalArea: P.select('original', P.not(P.nullish)),
        },
      },
      ({ cropped, original }) => ({
        x: original.topLeft.x - cropped.topLeft.x,
        y: 0,
      }),
    )
    .with({ macroOffset: P.select('offset', P.not(P.nullish)) }, ({ offset }) => offset)
    .otherwise(() => undefined);
};

// Returns the portrait width/height and rotation angle. macro.dimensions holds the displayed
// (landscape) size, so for a 90°/270° quarter-turn the pre-rotation portrait width is the
// landscape height (dimensions[1]) and the portrait height is the landscape width (dimensions[0]).
// AI handler computes macro contours against the pre-rotation, portrait-oriented image.
const determineMacroPortraitWidthAndRotation = (
  metadata: WSIPassportType,
): { portraitWidth: number; portraitHeight: number; rotation: number } | undefined => {
  return match(metadata)
    .with(
      { macro: { rotation: P.select('rotation', P.when((r) => r === 90 || r === 270)), dimensions: P.select('dimensions', P.not(P.nullish)) } },
      ({ rotation, dimensions }) => ({
        portraitWidth: dimensions[1],
        portraitHeight: dimensions[0],
        rotation,
      }),
    )
    .otherwise(() => undefined);
};

const buildContourMap = (measurements: MeasurementsType, metadata: WSIPassportType) => {
  const macroPortraitData = determineMacroPortraitWidthAndRotation(metadata);
  const macroPortraitWidth = macroPortraitData?.portraitWidth;
  const macroPortraitHeight = macroPortraitData?.portraitHeight;
  const rotation = macroPortraitData?.rotation;
  return {
    blur: generateGeoJsonFromContours(measurementContours.parse(measurements?.blur?.contours)),
    missing_tissue: generateGeoJsonFromContours(
      measurementContours.parse(measurements?.missing_tissue?.contours),
      determineMacroShift(metadata),
      macroPortraitWidth,
      macroPortraitHeight,
      rotation,
    ),
    tissue: generateGeoJsonFromContours(
      measurementContours.parse(measurements?.tissue?.contours),
    ),
    misaligned_cover_slip: generateGeoJsonFromContours(
      measurementContours.parse(measurements?.misaligned_cover_slip?.contours),
    ),
    cut_off_tissue_macro: measurements?.image_data_loss?.detected
      ? undefined
      : generateGeoJsonFromContours(
          measurementContours.parse(measurements?.cut_off_tissue?.contours_macro),
          determineMacroShift(metadata),
          macroPortraitWidth,
          macroPortraitHeight,
          rotation,
        ),
    cut_off_tissue_wsi: generateGeoJsonFromContours(
      measurementContours.parse(measurements?.cut_off_tissue?.contours_wsi),
    ),
    marker_on_top_of_tissue: generateGeoJsonFromContours(
      measurementContours.parse(measurements?.marker_on_top_of_tissue?.contours),
    ),
  };
};

export const generateMetadata = (
  aiInfo: AIHandlerResultType,
  metadata: WSIPassportType,
): SlideScanGeneratedMetadataType => {
  const macroPortraitData = determineMacroPortraitWidthAndRotation(metadata);
  return match(aiInfo.schema_version)
    .with(P.union('3.0.0', '3.1.0', '3.2.0', '3.3.0'), () => {
      const { measurements } = aiInfo as { measurements: MeasurementsType };
      return {
        schema_version: '1.0.0',
        contours: buildContourMap(measurements, metadata),
        patches: {
          ...(aiInfo.measurements?.blur ? { blur: aiInfo.measurements.blur.patches } : {}),
        },
        missing_tissue_bounding_box: generateGeoJsonFromContours(
          measurementContours.parse(measurements?.missing_tissue?.processed_region_contours),
          determineMacroShift(metadata),
        ),
      } satisfies SlideScanGeneratedMetadataType;
    })
    .with('3.4.0', () => {
      const { measurements } = aiInfo as { measurements: MeasurementsType };
      return {
        schema_version: '1.1.0',
        contours: {
          ...buildContourMap(measurements, metadata),
          image_data_loss: generateGeoJsonFromContours(
            measurementContours.parse(measurements?.image_data_loss?.contours),
          ),
          striping: generateGeoJsonFromContours(measurementContours.parse(measurements?.striping?.contours)),
        },
        patches: {
          ...(aiInfo.measurements?.blur ? { blur: aiInfo.measurements.blur.patches } : {}),
        },
        missing_tissue_bounding_box: generateGeoJsonFromContours(
          measurementContours.parse(measurements?.missing_tissue?.processed_region_contours),
          determineMacroShift(metadata),
          macroPortraitData?.portraitWidth,
          macroPortraitData?.portraitHeight,
          macroPortraitData?.rotation,
        ),
      } satisfies SlideScanGeneratedMetadataType;
    })
    .otherwise(() => {
      throw new Error(`Unsupported AI info schema version: ${aiInfo.schema_version}`);
    });
};
